import type { Route } from 'next';

import { ARTICLE_LIST_ITEM_SIZES, ArticleListItem } from '#components/Article/Items/ArticleListItem';
import { Image } from '#components/Image/Image';
import { buildBoardPath } from '#cutils/builders/buildBoardPath';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { useCurrentDate } from '#cutils/context/TimeContext';
import { TrackEventDataType } from '#types/tracking';
import { articleIsBreakingNews } from '#utils/article/articleIsBreakingNews';
import { stripTags } from '#utils/html/strip-tags';
import { logger } from '#utils/logging/logger';

import type { ArticleFooterFragment } from '../__generated__/ArticleFooter.generated';

type Props = {
	teaser: NonNullable<NonNullable<ArticleFooterFragment['links']>[number]>;
	className?: string;
};

export function ArticleSectionRelatedTeaser({ teaser, className }: Props) {
	const { url, label, meta, article, board } = teaser;
	const currentDate = useCurrentDate();
	const { images } = useEnvironment();

	if (!meta) {
		return null;
	}

	const { thumbnail, date } = meta;
	const title = meta.title || label;
	const { description } = { description: meta.description };

	const imageComp = <Image alt={title ?? ''} src={thumbnail ?? images.FALLBACK_IMAGE} sizes={ARTICLE_LIST_ITEM_SIZES} />;
	const internalTeaser = article ?? board;

	// if we have an article or a board it is an internal link
	if (internalTeaser) {
		const isArticleTeaser = !!article;

		const isBreakingNews = isArticleTeaser && articleIsBreakingNews(article, currentDate);
		const modulesTypes = isArticleTeaser ? article.modulesTypes : [];

		if (!internalTeaser.path) {
			logger.error(`Path for internal related teaser is not available for %s with id %s`, internalTeaser.__typename, internalTeaser.rowId);
			return null;
		}

		let path: Route;
		if (article) {
			path = article.path as Route;
		} else {
			// board is defined because internalTeaser is defined
			path = buildBoardPath(board!.rowId, board!.slug, board!.path);
		}

		return (
			<ArticleListItem
				as="h3"
				className={className}
				image={imageComp}
				publicationDate={date}
				teaserText={description}
				title={stripTags(title ?? '')}
				trackEventDataType={TrackEventDataType.ArticleRelatedTeasersClick}
				internalPath={path}
				isBreakingNews={isBreakingNews}
				modulesTypes={modulesTypes}
			/>
		);
	} else {
		if (!url) {
			logger.error(`Url for external related teaser is not defined %s`, teaser.meta?.title);
			return null;
		}

		return (
			<ArticleListItem
				as="h3"
				className={className}
				image={imageComp}
				publicationDate={date}
				teaserText={description}
				title={stripTags(title ?? '')}
				trackEventDataType={TrackEventDataType.ArticleRelatedTeasersClick}
				externalUrl={url as `${string}:${string}`}
			/>
		);
	}
}
